import React, { useState, useMemo, useCallback, useEffect, useContext } from 'react';

import { Button, DropdownButton, Card, Spinner, Toggle, Label, ScrollableContainer } from '@ergeon/core-components';
import { PlusIcon } from '@ergeon/icons/hero/micro';

import { StaffFilter } from '../StaffFilter';
import { StaffFilterContext } from '../../context-providers/StaffFilterContext';
import LoadingState from '../LoadingState';
import {
  getActivitiesFilter,
  setActivitiesFilter,
  getActivitiesImportantOnly,
  setActivitiesImportantOnly,
} from '../../utils';
import usePullActivity from '../../hooks/usePullActivity';
import useReturnActivityToQueue from '../../hooks/useReturnActivityToQueue';
import { useCentrifugoNotifications, useNotificationHandler } from '../../hooks/useCentrifugoNotifications';
import { ModalController } from '../../modals';
import ReturnToQueueModal from '../../modals/ReturnToQueueModal';
import { getCurrentUser } from '../../api';
import useFetch from '../../hooks/useFetch';
import { usePagination } from '../../hooks/usePagination';
import { useActivitiesSearch } from '../../hooks/useActivitiesSearch';
import { usePreservedSearchParams } from '../../hooks/usePreservedSearchParams';
import type { ActivityAPI } from '../../types';

import { ActivitiesList } from './components/ActivitiesList';
import type { Activity } from './types';
import {
  ActivityFilter,
  ACTIVITY_FILTER_VALUES,
  getFilterLabel,
  getFilterDropdownItems,
  filterActivities,
} from './utils/activitiesFilters';
import { convertAPIActivityToActivity } from './utils/activityUtils';

export const ActivitiesPageContent: React.FC = () => {
  const { selection, isSelectionInitialized } = useContext(StaffFilterContext);
  const [searchParams, setSearchParams] = usePreservedSearchParams();

  // Priority: URL > localStorage > default
  const urlFilter = searchParams.get('filter') as ActivityFilter | null;
  const storedFilter = getActivitiesFilter();
  const initialFilter = urlFilter && ACTIVITY_FILTER_VALUES.includes(urlFilter) ? urlFilter : storedFilter;

  // Priority: URL > localStorage > default
  const urlImportant = searchParams.get('important');
  const storedImportant = getActivitiesImportantOnly();
  let initialImportant: 'all' | 'important' = 'all';
  if (urlImportant === 'true') {
    initialImportant = 'important';
  } else if (urlImportant === 'false') {
    initialImportant = 'all';
  } else if (storedImportant) {
    initialImportant = 'important';
  }

  const [filter, setFilterState] = useState<ActivityFilter>(initialFilter);
  const [showImportant, setShowImportantState] = useState<'all' | 'important'>(initialImportant);

  // Update URL and localStorage when filter changes
  const setFilter = useCallback(
    (newFilter: ActivityFilter) => {
      setFilterState(newFilter);
      setActivitiesFilter(newFilter); // Save to localStorage
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('filter', newFilter);
        return params;
      });
    },
    [setSearchParams],
  );

  // Update URL and localStorage when showImportant changes
  const setShowImportant = useCallback(
    (newValue: 'all' | 'important') => {
      setShowImportantState(newValue);
      setActivitiesImportantOnly(newValue === 'important'); // Save to localStorage
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (newValue === 'important') {
          params.set('important', 'true');
        } else {
          params.delete('important');
        }
        return params;
      });
    },
    [setSearchParams],
  );

  // Set filter in URL on mount if not present (use stored or default)
  useEffect(() => {
    const urlFilter = searchParams.get('filter');
    if (!urlFilter) {
      const storedFilter = getActivitiesFilter();
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('filter', storedFilter);
        return params;
      }, { replace: true });
    }
  }, []);

  // Sync state with URL on mount and URL changes
  useEffect(() => {
    const urlFilter = searchParams.get('filter') as ActivityFilter | null;
    const urlImportant = searchParams.get('important');

    if (urlFilter && ACTIVITY_FILTER_VALUES.includes(urlFilter) && urlFilter !== filter) {
      setFilterState(urlFilter);
    }

    const newImportant = urlImportant === 'true' ? 'important' : 'all';
    if (newImportant !== showImportant) {
      setShowImportantState(newImportant);
    }
  }, [searchParams]);

  // Get current user to filter activities
  const fetchCurrentUser = useCallback(() => {
    return getCurrentUser();
  }, []);

  const { data: currentUser, isLoading: isLoadingUser } = useFetch<{
    id: number;
    gid: string;
    email: string;
    full_name: string;
    preferred_language: string;
  } | null>(fetchCurrentUser, [fetchCurrentUser]);

  // Get activities search functionality
  // Determine staffId based on selection:
  // - everyone: null (show all activities, no user filter)
  // - current_user: null for staffId, use currentUserId as fallback
  // - specific_staff: use the selected staff's ID
  const staffIdForActivities = useMemo(() => {
    if (selection.type === 'specific_staff') {
      return selection.staff.id;
    }
    return null;
  }, [selection]);

  // Only use currentUserId for current_user selection, not for everyone
  // Use selection.user.id from context (not local currentUser) to avoid refetch on getCurrentUser resolve
  const currentUserIdFallback = useMemo(() => {
    if (selection.type === 'current_user') {
      return selection.user.id;
    }
    return null; // For 'everyone', don't filter by user
  }, [selection]);

  const { fetchActivitiesPage, canPull } = useActivitiesSearch(
    filter,
    showImportant,
    staffIdForActivities,
    currentUserIdFallback,
  );

  // Setup pagination - wait until selection is initialized to fetch with correct filter
  const pagination = usePagination<ActivityAPI>({
    fetchPage: fetchActivitiesPage,
    initialPage: 1,
    enabled: isSelectionInitialized,
  });

  // Handle activities updates for all customers
  const handleActivitiesUpdated = useNotificationHandler(undefined, pagination.refetch);

  useCentrifugoNotifications({ onActivitiesUpdated: handleActivitiesUpdated });

  // Pull activity from queue
  const { pullActivityFromQueue, isLoading: isPulling, error: pullError } = usePullActivity(pagination.refetch);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  // Return activity to queue
  const { returnToQueue } = useReturnActivityToQueue(pagination.refetch);

  const handleGetActivity = useCallback(async() => {
    setFeedbackMessage(null);
    const count = await pullActivityFromQueue();
    if (count === 0) {
      setFeedbackMessage('No activities available in the queue');
    }
  }, [pullActivityFromQueue]);

  // Convert API activities to Activity type
  const activities: Activity[] = useMemo(() => {
    return pagination.data.map(convertAPIActivityToActivity);
  }, [pagination.data]);

  // Apply client-side filtering for date-based filters and important flag
  const filteredActivities = useMemo(() => {
    let result = filterActivities(activities, filter);

    if (showImportant === 'important') {
      result = result.filter((a) => a.isImportant);
    }

    return result;
  }, [activities, filter, showImportant]);

  const handleLoadMore = useCallback(async() => {
    if (!pagination.isLoading && pagination.hasMore) {
      try {
        await pagination.loadMore();
      } catch {
        // Errors are handled silently in usePagination
      }
    }
  }, [pagination]);

  const filterDropdownItems = useMemo(() => getFilterDropdownItems(filter, setFilter), [filter]);

  const handleReturnToQueue = useCallback((activityId: number) => {
    const activity = activities.find(a => a.id === activityId);
    if (!activity) return;

    ModalController.show(ReturnToQueueModal, {
      activityTitle: activity.title,
      onConfirm: () => returnToQueue(activityId),
    });
  }, [activities, returnToQueue]);

  const isLoadingData = pagination.isLoading || isLoadingUser || !isSelectionInitialized;
  const showEmptyState = !isLoadingData && filteredActivities.length === 0;

  const renderContent = () => {
    if (isLoadingData) {
      return <LoadingState />;
    }

    return (
      <ActivitiesList
        activities={filteredActivities}
        currentUserId={currentUser?.id}
        emptyStateProps={{
          isLoading: isPulling,
          message: pullError || feedbackMessage,
          onGetActivity: canPull ? handleGetActivity : undefined,
        }}
        hasMore={pagination.hasMore}
        isLoading={pagination.isLoading}
        onLoadMore={handleLoadMore}
        onReturnToQueue={handleReturnToQueue}
        showEmptyState={showEmptyState} />
    );
  };

  return (
    <Card className="flex flex-col gap-3 p-4 w-full h-full overflow-hidden" flavor="base" zIndex="1">
      <div className="flex justify-between items-center gap-4 py-1">
        <div className="flex items-center gap-3">
          <StaffFilter />
          <DropdownButton
            alignment="left"
            flavor="regular"
            hideChevron={false}
            items={filterDropdownItems}
            label={getFilterLabel(filter)}
            size="small"
            taste="boundless"
            title="Filter activities" />
        </div>

        <div className="flex items-center gap-4">
          <p className="text-gray-500 dark:text-gray-600 paragraph-3">
            {filteredActivities.length} {filteredActivities.length === 1 ? 'Activity' : 'Activities'} Shown
          </p>
          <div className="flex items-center gap-2">
            <Label>Important</Label>
            <Toggle
              items={[
                { label: '', value: 'all' as const },
                { label: '', value: 'important' as const },
              ]}
              onChange={(value) => setShowImportant(value as 'all' | 'important')}
              value={showImportant} />
          </div>
          {canPull && (
            <Button
              aria-label="Get an activity from the queue"
              data-testid="header-get-activity-button"
              disabled={isPulling}
              flavor="regular"
              onClick={handleGetActivity}
              size="medium"
              taste="line">
              {isPulling ? (
                <>
                  <Spinner active className="shrink-0" />
                  Getting...
                </>
              ) : (
                <>
                  <PlusIcon className="size-4 shrink-0" />
                  Get Activity
                </>
              )}
            </Button>
          )}
        </div>
      </div>
      <ScrollableContainer className="px-2.5" direction="col" maskSize={0}>
        {renderContent()}
      </ScrollableContainer>
    </Card>
  );
};
